import { useState, useEffect, useCallback } from 'react'
import AgentStatusBar from '@/components/agent/AgentStatusBar'
import SessionList from '@/components/agent/SessionList'
import ChatPanel from '@/components/agent/ChatPanel'
import useAgentWebSocket from '@/hooks/useAgentWebSocket'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'
import type { AgentSession, AgentMessage } from '@/hooks/useAgentWebSocket'

export default function AgentWorkspace() {
  const { connected, sessions, setSessions, messages, sendMessage, acceptSession, sendTyping, typingSessionIds } = useAgentWebSocket()
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [fetchedMessages, setFetchedMessages] = useState<Map<string, AgentMessage[]>>(new Map())
  const [myAgent, setMyAgent] = useState<{ currentChats: number; maxConcurrent: number } | null>(null)

  const selectedSession = sessions.find(s => s.id === selectedSessionId) || null

  // Merge WS messages with fetched history
  const currentMessages = [
    ...(fetchedMessages.get(selectedSessionId || '') || []),
    ...(messages.get(selectedSessionId || '') || []),
  ]

  // Deduplicate by id
  const deduped = Array.from(new Map(currentMessages.map(m => [m.id, m])).values())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  // Fetch message history when selecting a session
  const fetchHistory = useCallback(async (sessionId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken()
      const apiBase = API_BASE_URL
      const res = await fetch(`${apiBase}/agents/sessions/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setFetchedMessages(prev => {
          const next = new Map(prev)
          next.set(sessionId, data.messages || [])
          return next
        })
      }
    } catch {
      // Will rely on WS messages
    }
  }, [])

  function handleSelect(sessionId: string) {
    setSelectedSessionId(sessionId)
    // Clear unread - done locally for now
    if (!fetchedMessages.has(sessionId)) {
      fetchHistory(sessionId)
    }
  }

  // REST fallback for accept session
  const handleAccept = useCallback(async (sessionId: string) => {
    if (connected) {
      acceptSession(sessionId)
    } else {
      try {
        const token = await auth.currentUser?.getIdToken()
        const res = await fetch(`${API_BASE_URL}/agents/sessions/${sessionId}/accept`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
        if (res.ok) {
          setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, mode: 'human' as const } : s))
        }
      } catch (err) {
        console.error('Accept session failed:', err)
      }
    }
  }, [connected, acceptSession, setSessions])

  // REST fallback for send message
  const handleSendMessage = useCallback(async (sessionId: string, content: string) => {
    // 乐观更新：立即在本地显示消息
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      sessionId,
      role: 'agent' as const,
      content,
      timestamp: new Date().toISOString(),
      agentName: auth.currentUser?.displayName || auth.currentUser?.email || 'Agent',
    }
    setFetchedMessages(prev => {
      const next = new Map(prev)
      const list = next.get(sessionId) || []
      next.set(sessionId, [...list, optimisticMsg])
      return next
    })

    // 发送到服务器
    if (connected) {
      sendMessage(sessionId, content)
    } else {
      try {
        const token = await auth.currentUser?.getIdToken()
        const res = await fetch(`${API_BASE_URL}/agents/sessions/${sessionId}/messages`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        })
        if (res.ok) {
          const data = await res.json()
          // 用服务器返回的真实消息替换乐观消息
          setFetchedMessages(prev => {
            const next = new Map(prev)
            const list = (next.get(sessionId) || []).map(m =>
              m.id === optimisticMsg.id ? data.message : m
            )
            next.set(sessionId, list)
            return next
          })
        }
      } catch (err) {
        console.error('Send message failed:', err)
      }
    }
  }, [connected, sendMessage])

  // Also fetch initial session list + agent info via REST
  useEffect(() => {
    async function fetchInitial() {
      try {
        const token = await auth.currentUser?.getIdToken()
        const apiBase = API_BASE_URL

        // Get my agent info (currentChats + maxConcurrent)
        const meRes = await fetch(`${apiBase}/agents/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (meRes.ok) {
          const meData = await meRes.json()
          const a = meData.agent || meData
          if (a) setMyAgent({ currentChats: a.currentChats || 0, maxConcurrent: a.maxConcurrent || 5 })
        }

        // Get sessions
        const res = await fetch(`${apiBase}/agents/sessions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          const allSessions = [...(data.queued || []), ...(data.mine || [])]
          if (allSessions.length > 0) {
            setSessions(allSessions)
          }
        }
      } catch (err) {
        console.warn('Failed to fetch initial state:', err)
      }
    }
    fetchInitial()
  }, [])

  // Use agent's actual currentChats/maxConcurrent from server, fall back to computed from sessions
  const humanSessionsCount = sessions.filter(s => s.mode === 'human').length
  const myChats = myAgent?.currentChats ?? humanSessionsCount
  const maxConcurrent = myAgent?.maxConcurrent ?? 5

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <AgentStatusBar
        currentChats={myChats}
        maxConcurrent={maxConcurrent}
        connected={connected}
      />
      <div className="flex flex-1 min-h-0">
        <SessionList
          sessions={sessions}
          selectedId={selectedSessionId}
          onSelect={handleSelect}
          onAccept={handleAccept}
        />
        <ChatPanel
          session={selectedSession}
          messages={deduped}
          onSendMessage={handleSendMessage}
          onTyping={sendTyping}
          isTyping={selectedSessionId ? typingSessionIds.has(selectedSessionId) : false}
        />
      </div>
    </div>
  )
}
